import { api, ApiError } from "@/api/api";
import { Credentials } from "@/api/auth-types";
import { User } from "@/api/users-types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useNavigate } from "react-router";

export function useUser({ enabled }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["GET", "status"],
    queryFn: async () => (await api.auth.status.get()).user,
    enabled,
  });
}

export function useHandleUnauthenticated() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  function handleUnauthenticated() {
    queryClient.removeQueries();
    navigate("/login");
  }

  useEffect(() => {
    window.addEventListener("Unauthenticated", handleUnauthenticated);
    return () => {
      window.removeEventListener("Unauthenticated", handleUnauthenticated);
    };
  }, []);
}

export function useLogout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  async function logout() {
    await api.auth.logout.post();
    queryClient.removeQueries();
    navigate("/login");
  }

  return logout;
}

export function useLogin() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<User, ApiError, Credentials>({
    mutationKey: ["POST", "/auth"],
    mutationFn: api.auth.post,
    onSuccess: () => {
      queryClient.removeQueries();
      navigate("/");
    },
  });
}
